import type { LessonSummaryDto } from '@codeframe/api-contract';
import { Check, Lock } from 'lucide-react';
import { Link } from 'react-router';
import { useCourses } from '../api/hooks.js';
import { ErrorView, Loading, Page, PageHeader } from '../components/Page.js';
import { Badge, Meter, Panel, cx } from '../components/ui.js';
import s from './AcademyPage.module.css';

const COMING = ['Loops', 'Lists', 'Dictionaries', 'Strings', 'Errors & debugging', 'Objects & classes', 'Recursion', 'State machines', 'Algorithms & Big O'];

export function AcademyPage() {
  const { data, isLoading, error } = useCourses();
  if (isLoading) return <Loading />;
  if (error || !data) return <ErrorView error={error} />;
  const firstOpen = (lessons: readonly LessonSummaryDto[]) => lessons.find((l) => l.status === 'available' || l.status === 'in_progress')?.id;
  return (
    <Page>
      <PageHeader
        eyebrow="Academy"
        title="Learn to program your Frame"
        description="Every lesson teaches a real programming concept, then has you apply it to mech software. Concepts you master carry over to every language you learn later."
      />
      {data.map((course) => {
        const current = firstOpen(course.lessons);
        const completed = course.lessons.filter((l) => l.status === 'completed').length;
        return (
          <Panel
            key={course.id}
            label={course.language}
            title={course.title}
            actions={<div style={{ width: 220 }}><Meter value={completed} max={course.lessons.length} display={`${completed}/${course.lessons.length} lessons`} /></div>}
          >
            <p className="muted" style={{ marginTop: 0 }}>{course.description}</p>
            <div className={s.course}>
              {course.lessons.map((l, i) => {
                const body = (
                  <>
                    <span className={s.num}>{l.status === 'completed' ? <Check size={18} /> : l.status === 'locked' ? <Lock size={15} /> : i + 1}</span>
                    <span>
                      <div className={s.title}>{l.title}</div>
                      <div className="muted">{l.summary}</div>
                      <div className={s.meta}>
                        <Badge>{l.concept.replace(/_/g, ' ')}</Badge>
                        <span className="label">{l.estimatedMinutes} min</span>
                      </div>
                    </span>
                    <span className={s.right}>
                      <Badge tone={l.status === 'completed' ? 'ok' : l.id === current ? 'accent' : 'neutral'}>
                        {l.status === 'completed' ? 'Complete' : l.status === 'locked' ? 'Locked' : l.status === 'in_progress' ? 'In progress' : 'Start'}
                      </Badge>
                      <span className="label">
                        {l.stepsCompleted}/{l.stepsTotal} steps · +{l.rewards.playerXp} XP
                      </span>
                      {l.rewards.parts.map((p) => (
                        <Badge key={p.id} tone="ok">
                          Unlocks {p.name}
                        </Badge>
                      ))}
                    </span>
                  </>
                );
                const cls = cx(s.lesson, l.status === 'locked' && s.locked, l.status === 'completed' && s.done, l.id === current && s.current);
                return l.status === 'locked' ? (
                  <div key={l.id} className={cls} title={l.lockedReason}>
                    {body}
                  </div>
                ) : (
                  <Link key={l.id} to={`/academy/${l.id}`} className={cls}>
                    {body}
                  </Link>
                );
              })}
            </div>
          </Panel>
        );
      })}
      <div style={{ height: 16 }} />
      <Panel label="Curriculum roadmap" title="Coming next">
        <div className={s.future}>
          {COMING.map((c) => (
            <Badge key={c}>{c}</Badge>
          ))}
          <Badge tone="cyber">JavaScript</Badge>
          <Badge tone="cyber">Lua / Luau</Badge>
        </div>
      </Panel>
    </Page>
  );
}
